import { createFileRoute, Link } from '@tanstack/react-router'
import { useNavigate } from '@tanstack/react-router'
import { ColDef } from 'ag-grid-community'
import { useEffect, useMemo, useRef } from 'react'

import { Product, ProductStatus } from '@/api/admin-products-types'
import { useCreateProductBarcode, useDeleteProduct, useDeleteProductBarcode } from '@/api/api-mutations'
import { preloadGetProduct, useGetProduct, useListProductBarcodes } from '@/api/react-query'
import { AssetImage } from '@/components/base/asset-image'
import { Badge, StatusBadge } from '@/components/base/badge'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { IconButton } from '@/components/base/icon-button'
import { usePrompt, useTextPrompt } from '@/components/base/prompt'
import { SectionRow } from '@/components/base/section-row'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { Tooltip } from '@/components/base/tooltip'
import { SingleColumnPage } from '@/components/page-layout'
import { createActionsColumn, Table } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { PencilSquare } from '@/icons/pencil-square'
import { Plus } from '@/icons/plus'
import { SquareTwoStack } from '@/icons/square-two-stack'
import { Trash } from '@/icons/trash'
import { ConditionalRouteComponent } from '@/utils/route-utilities'

export const Route = createFileRoute('/_authenticated/_general/products/$productUuid')({
	component: () => (
		<ConditionalRouteComponent
			routePath={Route.id}
			pathsToRenderOn={['/organization', '/edit', '/prices', '/media']}
		>
			<ProductDetails />
		</ConditionalRouteComponent>
	),
	loader: async ({ context, params: { productUuid } }) =>
		await preloadGetProduct(context, {
			path: { productUuid }
		}),
	staticData: {
		crumb: ({ loaderData, context: { L } }) => loaderData?.title
	}
})

function ProductDetails() {
	const { L } = useI18n()
	const { productUuid } = Route.useParams()
	const product = useGetProduct({ productUuid })

	if (!product.data) {
		return null
	}
	return (
		<SingleColumnPage hasOutlet>
			<ProductGeneralSection product={product.data} />
			<ProductMediaSection product={product.data} />
			<ProductOrganizationSection product={product.data} />
			<PricingSection product={product.data} />
			<BarcodeSection uuid={productUuid} />
		</SingleColumnPage>
	)
}

const getProductStatusColor = (status: string) => {
	switch (status) {
		case ProductStatus.Draft:
			return 'blue'
		case ProductStatus.Published:
			return 'green'
		case ProductStatus.Archived:
			return 'grey'
		default:
			return 'grey'
	}
}

export const ProductGeneralSection = ({ product }: { product: Product }) => {
	const { L } = useI18n()
	const prompt = usePrompt()
	const navigate = useNavigate()

	const deleteProduct = useDeleteProduct()
	const handleDelete = async () => {
		const res = await prompt({
			title: L.General.AreYouSure(),
			description: L.Product.DeleteWarning({
				title: product.title
			}),
			confirmText: L.General.Delete(),
			cancelText: L.General.Cancel()
		})

		if (!res) {
			return
		}

		const result = await deleteProduct({ path: { productUuid: product.uuid } })
		if (result.isSuccess) {
			navigate({ to: '..' })
		}
	}

	const isSingleVariant = product.variants.length === 1
	return (
		<>
			<Container className='divide-y'>
				<ContainerHeader
					heading={product.title}
					rightSide={
						<>
							<StatusBadge color={getProductStatusColor(product.status)}>
								{L.Enum.ProductStatus(product.status.toUpperCase() as any)}
							</StatusBadge>
							<Link from={Route.fullPath} to='edit'>
								<IconButton>
									<PencilSquare />
								</IconButton>
							</Link>
						</>
					}
					actions={[
						{
							actions: [
								{
									label: L.General.Delete(),
									onClick: handleDelete,
									icon: <Trash />
								}
							]
						}
					]}
				/>
				<div className='flex flex-row divide-x'>
					<div className='flex flex-1 flex-col gap-y-2 divide-y p-0'>
						<SectionRow title={L.Product.Subtitle()} value={product.subtitle} />
						<SectionRow title={L.Product.Description()} value={product.description} />
						{!!product.externalId && (
							<SectionRow title={L.Product.ExternalId()} value={product.externalId} />
						)}
						{isSingleVariant && (
							<SectionRow
								title={L.Product.PricingMethodTitle()}
								value={L.Enum.PricingMethod(product.variants[0].pricingMethod)}
							/>
						)}
						{isSingleVariant && <SectionRow title={L.Product.Sku()} value={product.variants[0].sku} />}
					</div>
				</div>
			</Container>
		</>
	)
}

export const ProductMediaSection = ({ product }: { product: Product }) => {
	const { L } = useI18n()

	const images = product.images ?? []
	const hasImages = images.length > 0

	return (
		<Container className='divide-y'>
			<ContainerHeader
				heading={L.Product.Media.Title()}
				rightSide={
					<Link from={Route.fullPath} to='media'>
						<IconButton>
							<PencilSquare />
						</IconButton>
					</Link>
				}
			/>

			{hasImages ? (
				<div className='grid grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-4 px-6 py-4'>
					{images.map(image => (
						<Link
							key={image.uuid}
							from={Route.fullPath}
							to='media'
							className='group relative aspect-square size-full cursor-pointer overflow-hidden rounded-[8px] shadow-elevation-card-rest transition-fg hover:shadow-elevation-card-hover'
						>
							<AssetImage asset={image.asset} className='size-full object-contain' />
						</Link>
					))}
				</div>
			) : product.imageUrl ? (
				<div className='grid grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-4 px-6 py-4'>
					<Link
						from={Route.fullPath}
						to='media'
						className='group relative aspect-square size-full cursor-pointer overflow-hidden rounded-[8px] shadow-elevation-card-rest transition-fg hover:shadow-elevation-card-hover'
					>
						<img
							src={product.imageUrl}
							alt={`${product.title} image`}
							className='size-full object-contain'
						/>
					</Link>
				</div>
			) : (
				<div className='flex flex-col items-center gap-y-4 pb-8 pt-6'>
					<div className='flex flex-col items-center'>
						<Text size='small' leading='compact' weight='plus' className='text-ui-fg-subtle'>
							{L.Product.Media.EmptyState.Header()}
						</Text>
						<Text size='small' className='text-ui-fg-muted'>
							{L.Product.Media.EmptyState.Description()}
						</Text>
					</div>
					<Link from={Route.fullPath} to='media'>
						<Button size='small' variant='secondary'>
							{L.Product.Media.EmptyState.Action()}
						</Button>
					</Link>
				</div>
			)}
		</Container>
	)
}

type PricingRowData = {
	id: string
	salesChannelName: string
	priceWithTaxDisplay: string
	priceWithoutTaxDisplay: string
	taxRateDisplay: string
}

const PricingSection = ({ product }: { product: Product }) => {
	const { L } = useI18n()
	const firstVariant = product.variants?.[0]
	const salesChannels = product.salesChannels ?? []

	const pricingData = useMemo((): PricingRowData[] => {
		if (!firstVariant || salesChannels.length === 0) {
			return []
		}
		const pricingRows: PricingRowData[] = []

		for (const price of firstVariant.prices) {
			const channel = salesChannels.find(c => c.uuid === price.salesChannel.uuid)
			if (!channel) {
				continue
			}

			const currencyCode = price.currencyCode ?? ''

			let priceWithTaxDisplay = ''
			let priceWithoutTaxDisplay = ''
			if (price.priceInfo?.variablePrice) {
				priceWithTaxDisplay = L.Product.Pricing.VariablePrice()
				priceWithoutTaxDisplay = L.Product.Pricing.VariablePrice()
			} else if (price.priceInfo) {
				// Gross/net are computed server-side (canonical `taxAmount()`), so the dashboard
				// only renders the pre-derived breakdown to keep every surface consistent.
				priceWithTaxDisplay = L.Amount.Default({ amount: price.priceInfo.taxBreakdown.gross, currencyCode })
				priceWithoutTaxDisplay = L.Amount.Default({ amount: price.priceInfo.taxBreakdown.net, currencyCode })
			}

			const taxRateName = price.priceInfo?.taxRateName
			const taxRateDisplay = taxRateName !== undefined ? String(taxRateName) : '-'

			pricingRows.push({
				id: channel.uuid,
				salesChannelName: channel.name,
				priceWithTaxDisplay,
				priceWithoutTaxDisplay,
				taxRateDisplay
			})
		}

		return pricingRows
	}, [salesChannels, firstVariant, L])

	const columns = useMemo<ColDef<PricingRowData>[]>(
		() => [
			{
				headerName: L.Fields.SalesChannel(),
				field: 'salesChannelName',
				flex: 2
			},
			{
				headerName: L.Product.Pricing.PriceWithTax(),
				field: 'priceWithTaxDisplay',
				flex: 1
			},
			{
				headerName: L.Product.Pricing.PriceWithoutTax(),
				field: 'priceWithoutTaxDisplay',
				flex: 1
			},
			{
				headerName: L.Fields.TaxRate(),
				field: 'taxRateDisplay',
				flex: 1
			}
		],
		[L]
	)

	return (
		<Container className='divide-y overflow-hidden'>
			<ContainerHeader
				heading={L.Product.Pricing.Title()}
				rightSide={
					<Link from={Route.fullPath} to='prices'>
						<IconButton>
							<PencilSquare />
						</IconButton>
					</Link>
				}
			/>
			<Table<PricingRowData>
				data={pricingData}
				columnDefs={columns}
				className='z-0'
				domLayout='autoHeight'
				suppressRowTransform
				suppressCellFocus={true}
				emptyState={
					<div className='flex flex-col items-center gap-y-4 pb-8 pt-6'>
						<div className='flex flex-col items-center'>
							<Text size='small' leading='compact' weight='plus' className='text-ui-fg-subtle'>
								{L.Product.Pricing.EmptyState.Header()}
							</Text>
							<Text size='small' className='text-ui-fg-muted'>
								{L.Product.Pricing.EmptyState.Description()}
							</Text>
						</div>
						<Link from={Route.fullPath} to='prices'>
							<Button size='small' variant='secondary'>
								{L.Product.Pricing.EmptyState.Action()}
							</Button>
						</Link>
					</div>
				}
			/>
		</Container>
	)
}

type BarcodeRowData = {
	barcode: string
}

// Pick a bwip-js symbology from the barcode value. EAN/UPC need a valid
// checksum, so fall back to the always-renderable code128 when they don't fit.
const getBarcodeSymbology = (value: string): string => {
	if (/^\d{13}$/.test(value)) {
		return 'ean13'
	}
	if (/^\d{12}$/.test(value)) {
		return 'upca'
	}
	if (/^\d{8}$/.test(value)) {
		return 'ean8'
	}
	return 'code128'
}

function BarcodePreview({ value }: { value: string }) {
	const canvasRef = useRef<HTMLCanvasElement>(null)

	useEffect(() => {
		const canvas = canvasRef.current
		if (!canvas || !value) {
			return
		}
		const render = (bcid: string, fallback = true) => {
			import('bwip-js/browser')
				.then(({ default: bwipjs }) => {
					try {
						bwipjs.toCanvas(canvas, {
							bcid,
							text: value,
							scale: 2,
							height: 12,
							includetext: true,
							textxalign: 'center'
						})
					} catch {
						if (fallback && bcid !== 'code128') {
							render('code128', false)
						}
					}
				})
				.catch(() => {
					// Leave the preview blank if the barcode renderer fails to load.
				})
		}
		render(getBarcodeSymbology(value))
	}, [value])

	return (
		<div className='rounded bg-white p-2'>
			<canvas ref={canvasRef} />
		</div>
	)
}

export function BarcodeSection({ uuid }: { uuid: string }) {
	const { L } = useI18n()
	const barcodes = useListProductBarcodes({ productUuid: uuid })

	const barcodeRows: BarcodeRowData[] = useMemo(() => {
		return barcodes.data?.map(barcode => ({ barcode })) ?? []
	}, [barcodes.data])

	const createBarcode = useCreateProductBarcode()
	const deleteBarcode = useDeleteProductBarcode()
	const prompt = usePrompt()
	const textPrompt = useTextPrompt()

	const columns = useMemo<ColDef<BarcodeRowData>[]>(
		() => [
			{
				headerName: L.Product.Barcode.Barcode(),
				field: 'barcode',
				flex: 1,
				cellClass: 'select-text cursor-text',
				cellRenderer: ({ value }: { value: string }) => (
					<Tooltip content={<BarcodePreview value={value} />} side='right' maxWidth={320} className='p-0'>
						<span>{value}</span>
					</Tooltip>
				)
			},
			createActionsColumn([
				{
					icon: <SquareTwoStack className='text-ui-fg-subtle' />,
					onClick: async row => {
						await navigator.clipboard.writeText(row.barcode)
						toast.success(L.General.Copied())
					}
				},
				{
					icon: <Trash />,
					onClick: async row => {
						const res = await prompt({
							title: L.Product.Barcode.Delete.Title(),
							description: L.Product.Barcode.Delete.Description({
								barcode: row.barcode
							}),
							confirmText: L.General.Delete(),
							cancelText: L.General.Cancel()
						})

						if (!res) {
							return
						}

						await deleteBarcode({
							path: { productUuid: uuid, barcode: row.barcode }
						})
					}
				}
			])
		],
		[]
	)

	return (
		<Container className='divide-y overflow-hidden'>
			<ContainerHeader
				heading={L.Product.Barcode.Title()}
				rightSide={
					<IconButton
						onClick={async () => {
							const barcode = await textPrompt({
								title: L.Product.Barcode.AddBarcodePrompt.Title(),
								placeholder: L.Product.Barcode.AddBarcodePrompt.Placeholder(),
								confirmText: L.General.Add(),
								cancelText: L.General.Cancel(),
								isValid: value => {
									if (value.length === 0) {
										return L.General.Required()
									}
									if (barcodes.data?.includes(value)) {
										return L.Product.Barcode.AlreadyOnProduct()
									}
									return undefined
								}
							})
							if (barcode) {
								const result = await createBarcode({ body: { barcode }, path: { productUuid: uuid } })
								if (!result.isSuccess) {
									toast.error((result.response as any)?.message ?? L.Product.Barcode.AlreadyInUse())
								}
							}
						}}
					>
						<Plus />
					</IconButton>
				}
			/>
			<Table<BarcodeRowData>
				data={barcodeRows}
				columnDefs={columns}
				domLayout='autoHeight'
				suppressCellFocus={true}
			/>
		</Container>
	)
}

export const ProductOrganizationSection = ({ product }: { product: Product }) => {
	const { L } = useI18n()

	return (
		<Container className='divide-y p-0'>
			<ContainerHeader
				heading={L.Product.Organization.Header()}
				rightSide={
					<Link from={Route.fullPath} to='organization'>
						<IconButton>
							<PencilSquare />
						</IconButton>
					</Link>
				}
			/>
			<SectionRow
				title={L.Fields.Groups()}
				value={
					product.groups?.length
						? product.groups.map(group => (
								<OrganizationTag
									key={group.uuid}
									label={group.name}
									to={`/product-groups/${group.uuid}`}
								/>
							))
						: undefined
				}
			/>

			<SectionRow
				title={L.Fields.Categories()}
				value={
					product.categories?.length
						? product.categories.map(pcat => (
								<OrganizationTag
									key={pcat.uuid}
									label={pcat.name}
									to={`/product-categories/${pcat.uuid}`}
								/>
							))
						: undefined
				}
			/>
		</Container>
	)
}

const OrganizationTag = ({ label, to }: { label: string; to: string }) => {
	return (
		<Tooltip content={label}>
			<Badge size='2xsmall' className='block w-fit truncate' asChild>
				<Link to={to}>{label}</Link>
			</Badge>
		</Tooltip>
	)
}
